import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  FilterContext,
  IndicatorsType,
  TopicLevelType,
} from '../../../context';
import CardCommon from '@op/components/CardCommon';
import '../index.scss';
import { getLocale, useIntl, useLocation } from 'umi';
import {
  SymbolType,
  barIcon,
  blockIcon,
  lineIcon,
  tableIcon,
  pieIcon,
} from '@/pages/opinion/constants/chart';
import { BlockChart, PieChart } from '@/pages/opinion/pages/Performance/components/TopicKeyword/components';
import DataTable from './DataTable';
import { Select } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import { indicatorsOptions } from '@op/pages/Summary/constants';
import BarAndLine from './BarAndLine';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';

export default function TopicTrends({
  timeline,
  topicLevel,
  tableInfo,
  setIndicators,
  indicators,
  blockData,
  loading,
}: {
  timeline: { sumMap: any; seriesDataArray: Array<any>; xAxisData: Array<any> };
  topicLevel: TopicLevelType;
  tableInfo: { data: any; header: any };
  setIndicators: any;
  indicators: IndicatorsType;
  blockData: { data: Array<any>; total: number };
  loading: boolean;
}) {
  const { filter, transTopic, gameInfo, topicTree } = useContext(FilterContext);

  const { formatOptionsMessage } = useLanguage({ placement: 'left' });
  const { formatMessage } = useIntl();

  const [titleIcons, noShow] = useMemo(() => {
    const noShow = [
      'mentions_ratio',
      'sentiment',
      'negative_mentions',
      'engagement_ratio',
    ].includes(indicators);
    let result: Array<{ icon: string; symbol: SymbolType; }> = [
      lineIcon,
      barIcon,
      tableIcon,
    ];
    // 2024-10-12 隐藏情感分柱状图  添加其他tab的折线图
    if (indicators === 'sentiment') {
      result = result.filter((item) => item.symbol !== 'bar');
    }
    if (indicators === 'mentions') {
      result = [...result, blockIcon, pieIcon];
    }
    if (indicators === 'engagement') {
      result.push(pieIcon);
    }
    return [result, noShow];
  }, [indicators]);

  const location: any = useLocation();
  const [chartType, setChartType] = useState<SymbolType>(
    location?.state?.chartType || location?.query?.chartType || 'line',
  );

  useEffect(() => {
    setChartType(location?.state?.chartType || location?.query?.chartType || titleIcons[0].symbol);
  }, [indicators]);

  const translateTopic = useCallback(
    (value: string, level = topicLevel) => {
      return transTopic(value, level);
    },
    [topicLevel, transTopic, formatMessage],
  );

  const empty = useMemo(() => {
    const { seriesDataArray, xAxisData } = timeline;
    return seriesDataArray.length === 0 && xAxisData.length === 0;
  }, [timeline]);

  const isEn = getLocale() === 'en-US';

  const { download, downloading } = useDownloadParams();

  return (
    <CardCommon
      className="dbo-topic-trends-card"
      operationInfo={[chartType, setChartType]}
      operations={titleIcons}
      emptyHeight={310}
      loadingHeight={310}
      empty={empty}
      contentCard
      loading={loading}
      title={
        <Select
          appearance="button"
          boxClassName="dbo-topic-tracking-metric-select-box"
          options={formatOptionsMessage(indicatorsOptions)}
          value={indicators}
          button={(option: any) => {
            return (
              <div
                className={`select-button ${isEn ? 'select-button--en' : 'select-button--chi'
                  }`}
              >
                {formatMessage(
                  { id: 'Ranked By {value}' },
                  {
                    value: (
                      <span style={{ color: isEn ? '#000' : '' }}>
                        {option.oldText}
                      </span>
                    ),
                  },
                )}
              </div>
            );
          }}
          style={{
            width: 230,
            fontWeight: 400,
            marginTop: 10,
            marginBottom: 2,
          }}
          listWidth={230}
          onChange={(value: IndicatorsType) => {
            buriedBtnPoint('STO_index', value);
            setIndicators(value);
          }}
        ></Select>
      }
      hasDownload
      downloadLoading={downloading}
      handleDownloadOnClick={() => {
        download({
          key: 'TopicTrackingTrends',
          fileName: `Topic Tracking Trends-${indicators}`,
          filter,
          data: tableInfo.data || [],
          auxiliaryParams: {
            tableHeader: (tableInfo.header || []).map((item: string) => {
              return {
                title: transTopic(item, topicLevel),
                key: item,
              };
            }),
            indicators,
          },
        });
      }}
    >
      <>
        {chartType === 'table' && (
          <DataTable
            tableInfo={tableInfo}
            indicators={indicators}
            transTopic={translateTopic}
            dateType={filter.date_type}
          ></DataTable>
        )}
        {['bar', 'line'].includes(chartType) && (
          <BarAndLine
            chartType={chartType}
            height={310}
            sumMap={timeline.sumMap}
            dateType={filter.date_type}
            xAxisData={timeline.xAxisData}
            seriesDataArray={timeline.seriesDataArray}
            transTopic={translateTopic}
            indicators={indicators}
            noShow={noShow}
          ></BarAndLine>
        )}
        {chartType === 'block' && (
          <BlockChart
            data={blockData.data}
            height={310}
            topicLevel={topicLevel}
            filter={filter}
            gameInfo={gameInfo}
            transTopic={translateTopic}
            formatMessage={formatMessage}
            dealwithTopic={(value: string) => {
              if (topicLevel === 'L1') {
                return topicTree[value];
              }

              return [value];
            }}
            total={blockData.total}
          ></BlockChart>
        )}
        {chartType === 'pie' && (
          <PieChart
            data={blockData.data}
            height={310}
            topicLevel={topicLevel}
            filter={filter}
            transTopic={translateTopic}
            formatMessage={formatMessage}
            total={blockData.total}
          ></PieChart>
        )}
      </>
    </CardCommon>
  );
}
